import { useCallback, useMemo, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { extractColors } from 'extract-colors';
import { Upload, Trash2, ImageIcon, Check, Type, Sparkles, RefreshCw, Building2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useTemplateDesigner } from '../TemplateDesignerContext';
import { useApp } from '@/components/AppLayout';
import {
  getBusinessInitials,
  isPresetIcon,
  getPresetIconComponent,
  PRESET_ICONS,
  generateThemeFromPrimary,
  analyzeExtractedColors,
  type ExtractedColor,
} from '../utils';

export function LogoPanel() {
  const { settings, updateSettings, updateNestedSettings } = useTemplateDesigner();
  const { currentBusiness } = useApp();
  const { logo, colorTheme } = settings;

  // Extracted colors from uploaded logo (top 5 most prominent)
  const [extractedColors, setExtractedColors] = useState<ExtractedColor[]>([]);
  // Track which color we're on for cycling (-1 = not generated yet)
  const [colorIndex, setColorIndex] = useState(-1);
  // Track if user wants to customize (use different logo than business logo)
  const [wantsCustomLogo, setWantsCustomLogo] = useState(false);

  // Determine what logo to display
  const businessLogo = currentBusiness?.logo;
  const hasTemplateLogo = !!logo.url;
  const hasBusinessLogo = !!businessLogo;
  // Show business logo when: no template logo set, business logo exists, and user hasn't clicked "use different"
  const showingBusinessLogo = !hasTemplateLogo && hasBusinessLogo && !wantsCustomLogo;

  // Get business initials (1-2 characters) for the initials icon
  const businessInitials = useMemo(() => {
    return getBusinessInitials(currentBusiness?.name || '');
  }, [currentBusiness?.name]);

  // Theme color for icon backgrounds
  const themeColor = colorTheme.primary;

  // Extract colors when logo changes (only for uploaded images, not preset icons)
  useEffect(() => {
    if (!logo.url || logo.url.startsWith('icon:')) {
      setExtractedColors([]);
      setColorIndex(-1);
      return;
    }

    // Extract colors from the uploaded image
    extractColors(logo.url, { crossOrigin: 'anonymous' })
      .then((colors) => {
        // Filter out insignificant colors (< 3% of image area)
        // This removes anti-aliasing artifacts, compression noise, etc.
        const MIN_AREA_THRESHOLD = 0.03;

        // Map to our simple type, filter by area, sort by area (most prominent first)
        const sorted = colors
          .filter((c) => c.area >= MIN_AREA_THRESHOLD)
          .map((c) => ({ hex: c.hex.toLowerCase(), area: c.area }))
          .sort((a, b) => b.area - a.area);

        // Dedupe by hex (keep first occurrence which has largest area)
        const seen = new Set<string>();
        const deduped: ExtractedColor[] = [];
        for (const color of sorted) {
          if (!seen.has(color.hex) && deduped.length < 10) {
            seen.add(color.hex);
            deduped.push(color);
          }
        }

        // Use analyzeExtractedColors to filter out whites, grays, and other unusable brand colors
        const { usableColors } = analyzeExtractedColors(deduped);
        const unique = usableColors.slice(0, 5);

        setExtractedColors(unique);
        setColorIndex(-1); // Reset when new logo uploaded
      })
      .catch(() => {
        setExtractedColors([]);
        setColorIndex(-1);
      });
  }, [logo.url]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        updateNestedSettings('logo', { url: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  }, [updateNestedSettings]);

  // Generate theme from logo colors, cycling through on each click
  const generateThemeFromLogo = useCallback(() => {
    if (extractedColors.length === 0) return;

    // Cycle to next color (or start at 0 if first time)
    const nextIndex = colorIndex === -1 ? 0 : (colorIndex + 1) % extractedColors.length;
    setColorIndex(nextIndex);

    const selectedColor = extractedColors[nextIndex];
    const palette = generateThemeFromPrimary(selectedColor.hex);
    updateSettings('colorTheme', palette);
  }, [extractedColors, colorIndex, updateSettings]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp'],
    },
    maxFiles: 1,
  });

  const removeLogo = () => {
    updateNestedSettings('logo', { url: null });
  };

  return (
    <div className="space-y-6">
      {/* Company Name Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <Type className="w-4 h-4 text-primary" />
            Show Company Name
          </Label>
          <Switch
            checked={logo.showCompanyName}
            onCheckedChange={(checked) => updateNestedSettings('logo', { showCompanyName: checked })}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Display your company name prominently on documents. Size adjusts automatically per template.
        </p>
      </div>

      {/* Upload Area */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-primary" />
          Logo / Icon {logo.showCompanyName && <span className="text-xs font-normal text-muted-foreground">(optional)</span>}
        </Label>

        {/* Case 1: Template-specific logo is set */}
        {logo.url ? (
          <div className="relative group">
            <div className="relative rounded-xl border-2 border-primary/30 bg-linear-to-br from-muted/50 to-muted p-6 flex items-center justify-center overflow-hidden min-h-30">
              {isPresetIcon(logo.url) ? (
                // Render the Lucide icon for preset icons (or letter for initials)
                (() => {
                  const iconId = logo.url?.replace('icon:', '') ?? '';
                  const IconComponent = getPresetIconComponent(iconId);

                  // Special case: initials shows the business initials
                  if (iconId === 'initials') {
                    // Adjust font size: smaller for 2 chars
                    const fontSize = businessInitials.length === 1 ? logo.size * 0.5 : logo.size * 0.35;
                    return (
                      <div
                        className="flex items-center justify-center rounded-xl text-white"
                        style={{
                          width: `${logo.size}px`,
                          height: `${logo.size}px`,
                          backgroundColor: themeColor,
                        }}
                      >
                        <span className="font-semibold" style={{ fontSize: `${fontSize}px` }}>{businessInitials}</span>
                      </div>
                    );
                  }

                  return IconComponent ? (
                    <div
                      className="flex items-center justify-center rounded-xl text-white"
                      style={{
                        width: `${logo.size}px`,
                        height: `${logo.size}px`,
                        backgroundColor: themeColor,
                      }}
                    >
                      <IconComponent style={{ width: '60%', height: '60%' }} />
                    </div>
                  ) : null;
                })()
              ) : (
                // Render uploaded image
                <img
                  src={logo.url}
                  alt="Logo preview"
                  className="max-h-24 max-w-full object-contain"
                  style={{ width: `${logo.size}%` }}
                />
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="secondary"
                  size="sm"
                  {...getRootProps()}
                  className="shadow-lg"
                >
                  <input {...getInputProps()} />
                  <Upload className="w-4 h-4 mr-1" />
                  Replace
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={removeLogo}
                  className="shadow-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Generate theme from logo */}
            {extractedColors.length > 0 && (
              <div className="mt-3 p-3 rounded-lg bg-linear-to-br from-primary/5 to-muted/50 border border-primary/20">
                <div className="flex items-center gap-2 mb-2.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs font-medium text-foreground">Generate theme from logo</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateThemeFromLogo}
                  className="w-full"
                >
                  {colorIndex === -1 ? (
                    'Generate'
                  ) : (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                      Regenerate
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        ) : showingBusinessLogo ? (
          /* Case 2: No template logo, but business logo exists - show it as default */
          <div className="space-y-3">
            <div className="relative rounded-xl border-2 border-emerald-200 bg-linear-to-br from-emerald-50/50 to-muted p-6 flex items-center justify-center overflow-hidden min-h-30">
              <img
                src={businessLogo}
                alt="Business logo"
                className="max-h-24 max-w-full object-contain"
                style={{ width: `${logo.size}%` }}
              />
            </div>
            <div className="flex items-center gap-2 px-1">
              <Building2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span className="text-xs text-emerald-700 font-medium">Using your business logo</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWantsCustomLogo(true)}
              className="w-full"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" />
              Use a different logo
            </Button>
          </div>
        ) : (
          /* Case 3: No logo at all - show upload dropzone */
          <div
            {...getRootProps()}
            className={`
              relative rounded-xl border-2 border-dashed p-8 text-center cursor-pointer
              transition-all duration-300 ease-out
              ${isDragActive
                ? 'border-primary bg-primary/5 scale-[1.02]'
                : 'border-border hover:border-primary/50 hover:bg-muted/50'
              }
            `}
          >
            <input {...getInputProps()} />
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-linear-to-br from-primary/20 to-primary/5 mb-3">
              <Upload className={`w-6 h-6 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
            </div>
            <p className="text-sm font-medium text-foreground">
              {isDragActive ? 'Drop your logo here' : 'Drag & drop your logo'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              or click to browse (PNG, JPG, SVG)
            </p>
            {/* If user clicked "use different" but business logo exists, show option to go back */}
            {wantsCustomLogo && hasBusinessLogo && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setWantsCustomLogo(false);
                }}
                className="mt-3"
              >
                Use business logo instead
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Preset Icons */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-primary" />
          Placeholder Icons
        </Label>
        <div className="grid grid-cols-5 gap-2">
          {PRESET_ICONS.map((preset) => {
            const Icon = preset.icon;
            const isSelected = logo.url === `icon:${preset.id}`;
            return (
              <button
                key={preset.id}
                onClick={() => updateNestedSettings('logo', { url: `icon:${preset.id}` })}
                className={`
                  relative aspect-square rounded-lg border-2 flex items-center justify-center transition-all
                  ${isSelected
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-card hover:bg-accent hover:border-primary/30'
                  }
                `}
                title={preset.name}
              >
                {isSelected && (
                  <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                    <Check className="w-3 h-3" />
                  </div>
                )}
                {preset.id === 'initials' ? (
                  // Show the business initials for initials option
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center font-bold"
                    style={{
                      backgroundColor: isSelected ? themeColor : 'rgb(156 163 175 / 0.2)',
                      color: isSelected ? 'white' : 'rgb(107 114 128)',
                      fontSize: businessInitials.length === 1 ? '0.75rem' : '0.625rem',
                    }}
                  >
                    {businessInitials}
                  </div>
                ) : Icon ? (
                  <Icon className={`w-6 h-6 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                ) : null}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          Use these as placeholders until you upload your own logo
        </p>
      </div>

      {/* Size Slider */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold">Size</Label>
          <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
            {logo.size}%
          </span>
        </div>
        <Slider
          value={[logo.size]}
          onValueChange={([value]) => updateNestedSettings('logo', { size: value })}
          min={20}
          max={100}
          step={5}
          className="py-2"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Small</span>
          <span>Large</span>
        </div>
      </div>
    </div>
  );
}
